import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';

const AuthenticatedHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { userProfile, profileLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Derive user role from AuthContext userProfile
  const userRole = userProfile?.role || 'partner';

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location?.pathname]);

  const handleLogout = async () => {
    try {
      const { error } = await authService?.signOut();
      
      if (error) {
        console.error('Logout error:', error);
        return;
      }

      // Navigate to partner authentication page after successful logout
      navigate('/partner-authentication');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const partnerNavItems = [
    { label: 'Dashboard', path: '/partner-dashboard', icon: 'LayoutDashboard' },
    { label: 'Create Referral', path: '/create-lead', icon: 'Plus' },
    { label: 'Profile', path: '/partner-profile', icon: 'User' },
  ];

  const adminNavItems = [
    { label: 'Dashboard', path: '/partner-dashboard', icon: 'LayoutDashboard' },
    { label: 'Partner Management', path: '/admin-partner-management', icon: 'Users' },
  ];

  const navItems = userRole === 'admin' ? adminNavItems : partnerNavItems;

  const isActive = (path) => location?.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-[1000] bg-card border-b border-border">
      <div className="flex items-center justify-between h-16 px-6">
        <Link to={userRole === 'admin' ? '/partner-dashboard' : '/partner-dashboard'} className="flex items-center gap-3">
          <div className="w-[200px] h-[200px] rounded-lg flex items-center justify-center transition-all duration-200">
            <img 
              src="/assets/images/Blue_Logo_-_Full_Name-1765020226497.png" 
              alt="Clear Environmental Services logo - horizontal layout with blue water droplet icon on left, 'clear' in bold navy blue and 'ENVIRONMENTAL SERVICES' in gray text on right, separated by vertical line" 
              className="w-[200px] h-[200px] object-contain"
            />
          </div>
          <span className="text-lg font-semibold text-foreground">Partner Portal</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-2">
          {navItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                isActive(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={item?.icon} size={18} />
              <span>{item?.label}</span>
            </Link>
          ))}
          <Button
            variant="ghost"
            size="sm"
            iconName="LogOut"
            iconPosition="left"
            onClick={handleLogout}
            className="ml-2"
          >
            Logout
          </Button>
        </nav>

        <button
          onClick={toggleMobileMenu}
          className="lg:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
          aria-label="Toggle mobile menu"
        >
          <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
        </button>
      </div>
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 z-[1100] bg-primary animate-fade-in">
          <nav className="flex flex-col p-4 gap-2">
            {navItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                  isActive(item?.path)
                    ? 'bg-white/20 text-white' :'text-white hover:bg-white/10'
                }`}
              >
                <Icon name={item?.icon} size={20} />
                <span>{item?.label}</span>
              </Link>
            ))}
            <Button
              variant="ghost"
              iconName="LogOut"
              iconPosition="left"
              onClick={handleLogout}
              fullWidth
              className="mt-4 text-white hover:bg-white/10 border-white/20"
            >
              Logout
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default AuthenticatedHeader;